import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppLink, Avatar, Drawer, DrawerSection, Tag, errorText, formatDateTime, isApiError } from "@amber/ui";
import type { QuickAction, TaskRow } from "../../contracts/index";
import { taskQuery } from "../queries";
import { waitingFor } from "../shared";
import { LEVEL_TEXT, PriorityCell } from "./PriorityCell";
import { QuickActions } from "./QuickActions";
import { IncidentFacts, RecordRecoveryDialog, RecoverySection, SeverityTag } from "./IncidentParts";

function whoCanAct(role: TaskRow["lowest_role"]): string {
  if (role === "admin") return "Admins";
  return role === "operator" ? "operators and admins" : "editors and above";
}

function PrioritySection({ priority, blocks }: { priority: NonNullable<TaskRow["priority"]>; blocks: string }) {
  return (
    <DrawerSection title="Priority" note="computed, not editable">
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md [&_dd]:m-0 [&_dt]:text-muted">
        <dt>Level</dt>
        <dd>
          {LEVEL_TEXT[priority.level]} · ranked {priority.rank} of {priority.of}
        </dd>
        <dt>Type</dt>
        <dd>
          {priority.type_label} · +{priority.type_weight}
        </dd>
        <dt>Age</dt>
        <dd>
          {priority.age_days === 1 ? "1 day" : `${String(priority.age_days)} days`} · +{priority.age}
        </dd>
        <dt>Impact</dt>
        <dd>
          {priority.people === 1 ? "1 person" : `${String(priority.people)} people`} +{priority.impact_people}
          {priority.items > 0 ? ` · ${String(priority.items)} items +${String(priority.impact_items)}` : ""}
        </dd>
        <dt>Blocks</dt>
        <dd>{blocks}</dd>
      </dl>
    </DrawerSection>
  );
}

export function TaskDrawer({
  id,
  onClose,
  onAction,
}: {
  id: string;
  onClose: () => void;
  onAction: (task: TaskRow, action: QuickAction) => void;
}) {
  const query = useQuery(taskQuery(id));
  const [recording, setRecording] = useState(false);
  const detail = query.data;
  if (!detail) {
    return (
      <Drawer title={query.isError ? "Task not found" : "Loading task…"} onClose={onClose}>
        {query.isError ? (
          <p className="m-0 text-muted">
            {isApiError(query.error) && query.error.status === 404
              ? "No task with this ID. It may have been closed more than 30 days ago."
              : errorText(query.error)}
          </p>
        ) : null}
      </Drawer>
    );
  }
  const task = detail.task;
  return (
    <Drawer title={task.title} subtitle={task.context} onClose={onClose}>
      <div className="flex flex-col gap-4 text-ui">
        <div className="flex flex-wrap items-center gap-1.5">
          <Tag tone="strong">{task.type_label}</Tag>
          <SeverityTag severity={detail.severity} />
          {task.priority ? (
            <PriorityCell priority={task.priority} blocks={detail.blocks} openedAt={task.opened_at} />
          ) : (
            <Tag tone="quiet">Done</Tag>
          )}
          {task.people.map((p) => (
            <span
              key={p.key}
              className="inline-flex items-center gap-1 rounded-full border border-divider py-px pr-2 pl-0.5 text-md"
            >
              <Avatar name={p.name} />
              {p.name}
            </span>
          ))}
          {task.platforms.map((p) => (
            <Tag key={p}>{p}</Tag>
          ))}
        </div>
        <DrawerSection title="What happened">
          <p className="m-0">{detail.what_happened}</p>
          <IncidentFacts detail={detail} />
        </DrawerSection>
        <DrawerSection title="Affected" note={task.affected.label}>
          {detail.affected_list.length === 0 ? (
            <p className="m-0 text-muted">{task.affected.label}</p>
          ) : (
            <ul className="m-0 list-none divide-y divide-divider rounded-lg border border-divider p-0">
              {detail.affected_list.map((a) => (
                <li key={`${a.label}-${a.detail}`} className="px-3 py-2">
                  {a.href ? (
                    <AppLink href={a.href} className="font-strong text-link">
                      {a.label}
                    </AppLink>
                  ) : (
                    <span className="font-strong">{a.label}</span>
                  )}
                  {a.detail ? <div className="text-md text-muted">{a.detail}</div> : null}
                </li>
              ))}
            </ul>
          )}
        </DrawerSection>
        {task.state === "open" ? (
          <DrawerSection title="Suggested next step">
            <p className="m-0 rounded-lg bg-surface-2 px-3 py-2.5">{detail.next_step}</p>
            <div className="mt-2.5">
              {task.can_act ? (
                <QuickActions
                  task={task}
                  size="md"
                  onAction={(a) => {
                    onAction(task, a);
                  }}
                />
              ) : (
                <p className="m-0 text-md text-muted">
                  Read-only for your role · {whoCanAct(task.lowest_role)} can act on this
                </p>
              )}
            </div>
          </DrawerSection>
        ) : null}
        {task.closed ? (
          <DrawerSection title="Resolved">
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-md [&_dd]:m-0 [&_dt]:text-muted">
              <dt>How</dt>
              <dd>{task.closed.how === "closed_automatically" ? "Closed automatically" : "Resolved by a person"}</dd>
              <dt>By</dt>
              <dd>{task.closed.by}</dd>
              <dt>Reason</dt>
              <dd>{task.closed.reason}</dd>
              <dt>When</dt>
              <dd>
                {formatDateTime(task.closed.at)} · open for {waitingFor(task.opened_at, new Date(task.closed.at))}
              </dd>
            </dl>
          </DrawerSection>
        ) : null}
        <RecoverySection
          detail={detail}
          onRecord={() => {
            setRecording(true);
          }}
        />
        {task.priority ? <PrioritySection priority={task.priority} blocks={detail.blocks} /> : null}
        <DrawerSection title="History">
          <ol className="m-0 list-none p-0 text-md">
            {detail.history.map((h) => (
              <li key={`${h.at}-${h.text}`} className="border-l-2 border-divider py-1 pl-3">
                <span className="text-muted">{formatDateTime(h.at)}</span> · {h.actor}: {h.text}
                {h.reason ? <div className="text-muted">“{h.reason}”</div> : null}
              </li>
            ))}
          </ol>
        </DrawerSection>
      </div>
      {recording ? (
        <RecordRecoveryDialog
          detail={detail}
          onClose={() => {
            setRecording(false);
          }}
        />
      ) : null}
    </Drawer>
  );
}
